import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, Output, inject } from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { Router } from '@angular/router'
import { LandingPageComponent } from '@app/modules/event-landing-page/components/landing-page/landing-page.component'
import { AuthService } from '@auth/services/auth.service'
import { environment } from '@environments/environment'
import { ClientRepresentedEvent } from '@events/models/event.model'
import { NbButtonModule } from '@nebular/theme'
import { WizardService } from '@app/v2/shared/components/wizard/services/wizard.service'
import { Persona } from '../../interfaces/persona.interface'
import { SharableCardComponent } from './components/sharable-card/sharable-card.component'
/**
 * Component that handles personas sharable links
 */
/* eslint-disable @angular-eslint/no-output-on-prefix */
@Component({
  selector: 'ngx-personas-share',
  templateUrl: './share.component.html',
  styleUrls: ['./share.component.scss'],
  imports: [CommonModule, MatIconModule, SharableCardComponent, NbButtonModule]
})
export class ShareComponent {
  selectedPersona
  tooltip = "Personas that require approval can't have self registration"
  /**
   * List of all personas and links
   */
  private _personas: { name: string; formRegistration: string; trafficDashboard: string; embedLink: string; marketingDashboard: string; slug: string; require_approval: boolean }[]
  /**
   * Dialog mode (smaller in size)
   */
  @Input()
  public dialogMode = false
  /**
   * Barcode mode (when barcode is clicked)
   */
  barcodeMode = false
  /**
   * Self services mode
   */
  selfServicesMode = false
  /**
   * Barcode link
   */
  barcodeLink: string
  /**
   * Barcode persona name
   */
  barcodePersona: string
  /**
   * Dialog closed
   */
  @Output()
  onClose: EventEmitter<boolean> = new EventEmitter<boolean>()
  /**
   * Event model
   */
  @Input()
  event: ClientRepresentedEvent
  selfServicePersona: Persona
  /**
   * Sets the current personas
   */
  @Input()
  set personas(
    value: Partial<{
      id: number
      name: string
      slug: string
      type: number
      weight: number
      formRegistration: string
      trafficDashboard: string
      embedLink: string
      marketingDashboard: string
      options: any
      products_count: number
      enable_payment: boolean
      require_approval: boolean
    }>[]
  ) {
    this._personas = []
    value
      .sort((a, b) => (a.weight < b.weight ? -1 : 1))
      .forEach((persona) => {
        const displayPersona = {
          id: persona.id,
          name: persona.name,
          formRegistration: '',
          trafficDashboard: '',
          embedLink: '',
          marketingDashboard: '',
          slug: '',
          options: {},
          products_count: 0,
          enable_payment: false,
          require_approval: false,
          type: null
        }
        displayPersona.type = persona.type
        displayPersona.enable_payment = persona.enable_payment
        displayPersona.products_count = persona.products_count
        displayPersona.slug = persona.slug
        displayPersona.options = persona.options
        displayPersona.formRegistration = `${environment.publicFormsUrl}workspaces/${this.auth.currentWorkspace.slug}/events/${this.event.slug}/forms/${persona.slug}`
        displayPersona.marketingDashboard = `${environment.dashboardsBaseUrl}marketing/${this.auth.currentWorkspace.slug}/${this.event.slug}`
        displayPersona.embedLink = `
        <div style="width:100%;height:100vh;">
          <script type="text/javascript" src="${environment.baseFormsUrl}/form.min.js"></script>
          <a class="contactless-registration-widget" data-width="100%" data-height="100%" href="${environment.publicFormsUrl}workspaces/${this.auth.currentWorkspace.slug}/events/${this.event.slug}/forms/${persona.slug}?ctl_campaign=${this.event.slug}&focus=false">${persona.name} Registration</a>
              <script type="text/javascript">
      addEventListener("message", (e) => {
        const _l = "${environment.baseFormsUrl}";
        if (_l === e.origin && e.data.redirectUrl) window.top.location.href = e.data.redirectUrl;
      });
    </script>
        </div>
       `
        displayPersona.trafficDashboard = `${environment.dashboardsBaseUrl}traffic/${this.auth.currentWorkspace.slug}/${this.event.slug}/default`
        displayPersona.require_approval = persona.require_approval

        if (persona.type !== 100) {
          this._personas.push(displayPersona)
        }
      })
  }
  /**
   * Gets the current personas
   */
  get personas(): Partial<{
    id: number
    name: string
    slug: string
    type: number
    weight: number
    formRegistration: string
    trafficDashboard: string
    embedLink: string
    marketingDashboard: string
    options: any
    products_count: number
    enable_payment: boolean
    require_approval: boolean
  }>[] {
    return this._personas
  }
  /**
   * Creates the component and injects it's dependencies
   * @param router Router
   * @param auth AuthService
   */
  constructor(
    private router: Router,
    private auth: AuthService
  ) {}

  private readonly _wizardService = inject(WizardService)

  /**
   * v2: close current wizard, open landing page setup (same as timeline dropdown).
   */
  openLandingPage(persona: { id: number }): void {
    if (!persona?.id || !this.event?.id) return

    const eventId = this.event.id
    const eventName = this.event.name
    const personaId = persona.id

    this._wizardService.closeAllWizardLayers()
    this._wizardService.open({
      component: LandingPageComponent,
      componentInputs: {
        event_id: eventId,
        persona_id: personaId
      },
      headerConfig: {
        breadcrumbs: [eventName, 'Landing Page']
      }
    })

    if (this.dialogMode) {
      this.onClose.next(false)
    }
  }

  /**
   * BarcodeClickEmitted handler
   * @param barcodeInfo
   */
  showBarcodeContent(barcodeInfo) {
    this.barcodeLink = barcodeInfo.url
    this.barcodePersona = barcodeInfo.persona
    this.switchBarcodeMode()
  }

  showSelfServicesContent(persona) {
    this.selfServicePersona = persona
    this.switchSelfServicesMode()
  }
  /**
   * Switch barcode mode
   */
  switchBarcodeMode() {
    this.barcodeMode = this.barcodeMode ? false : true
  }

  switchSelfServicesMode() {
    this.selfServicesMode = this.selfServicesMode ? false : true
  }
  /**
   * Download the barcode blob
   */
  download() {
    fetch(this.barcodeLink, { method: 'GET' })
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${this.event.name} - ${this.barcodePersona}+ QR Code.png`
        document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
        a.click()
        a.remove() //afterwards we remove the element again    )
      })
  }
  /**
   * Closes the dialog or navigates to event timeline
   */
  dismiss() {
    if (this.dialogMode) {
      this.onClose.next(true)
    } else {
      this.router.navigate(['events', 'list'])
    }
  }
  /**
   * Opens a new tab for either self registration or self print based on the passed parameter
   * @param service string
   */
  redirect(service: string) {
    let link = ''
    switch (service) {
      case 'SELF_REG':
        link = `${environment.publicFormsUrl}self-registrations/${this.auth.currentWorkspace.slug}/${this.event.slug}?form=${this.selfServicePersona.slug}`
        break
      case 'SELF_PRNT':
        link = `${environment.publicFormsUrl}self-print/${this.auth.currentWorkspace.slug}/${this.event.slug}`
        break
      default:
        break
    }
    if (link) {
      window.open(link, '_blank')
    }
  }
  setClickedPersona($event) {
    this.selectedPersona = $event
    if (this.selectedPersona.type == 30) {
      this.tooltip = 'Self registration can not be enabled in group forms'
    }
  }
  allowSelfRegisteration() {
    return !this.selectedPersona?.require_approval && this.selectedPersona?.type != 30
  }
}
